/**
 * src/pages/SupportCenter.js
 *
 * Help Center browse guides, FAQ, support tickets, and contact form.
 */

import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Search,
  X,
  BookOpen,
  HelpCircle,
  TicketCheck,
  Mail,
  Compass,
  ShoppingBag,
  Heart,
  BarChart3,
  Award,
  Shield,
  ChevronRight,
  ArrowRight,
  ArrowLeft,
} from "lucide-react";
import { getCategories, getFaqItems, getGuides, searchHelp } from "../data/helpData";
import FAQ from "../components/FAQ";
import ContactForm from "../components/ContactForm";
import NoData from "../components/NoData";
import { useAuth } from "../hooks/useAuth";
import { useTickets } from "../hooks/useSupport";
import TicketCard from "../components/support/TicketCard";
import { getSupportChatForUser } from "../services/chatService";
import { errorDev } from '../utils/logger.js';

const CATEGORY_ICONS = {
  "getting-started": Compass,
  marketplace: ShoppingBag,
  "market-data": BarChart3,
  impact: Award,
  account: Shield,
};

export default function SupportCenter() {
  const navigate = useNavigate();
  const { t, i18n, ready: i18nReady } = useTranslation("support");
  const lang = i18n.language?.split("-")[0] || "en";
  const { user, loading: authLoading } = useAuth();

  const [tab, setTab] = useState("guides");
  const [searchQuery, setSearchQuery] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [categoryFilter, setCategoryFilter] = useState(null);
  const [supportChatLoading, setSupportChatLoading] = useState(false);
  const [supportFallbackNotice, setSupportFallbackNotice] = useState("");
  const [fallbackNavTimer, setFallbackNavTimer] = useState(null);

  useEffect(() => () => {
    if (fallbackNavTimer) clearTimeout(fallbackNavTimer);
  }, [fallbackNavTimer]);

  const safeT = useCallback(
    (key, fallback) => {
      if (!i18nReady) return fallback;
      try {
        const result = t(key);
        return result === key ? fallback : result;
      } catch (err) {
        errorDev("Operation failed:", err);
        return fallback;
      }
    },
    [t, i18nReady]
  );

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => setSearchQuery(searchInput.trim()), 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // Data
  const categories = useMemo(() => getCategories(lang), [lang]);
  const guides = useMemo(
    () => getGuides(lang, categoryFilter),
    [lang, categoryFilter]
  );
  const faqItems = useMemo(
    () => getFaqItems(lang, categoryFilter),
    [lang, categoryFilter]
  );

  // Search results
  const searchResults = useMemo(() => {
    if (!searchQuery) return null;
    return searchHelp(searchQuery, lang);
  }, [searchQuery, lang]);

  // Tickets (real-time from Firestore)
  const [tickets, ticketsLoading] = useTickets();

  const tabs = [
    { id: "guides", label: safeT("tabs.guides", "Guides"), Icon: BookOpen },
    { id: "faq", label: safeT("tabs.faq", "FAQ"), Icon: HelpCircle },
    { id: "tickets", label: safeT("tabs.tickets", "My Tickets"), Icon: TicketCheck, count: tickets?.length || 0 },
    { id: "contact", label: safeT("tabs.contact", "Contact"), Icon: Mail },
  ];

  const openLiveSupport = useCallback(async () => {
    if (!user?.uid) {
      navigate("/login");
      return;
    }
    setSupportChatLoading(true);
    setSupportFallbackNotice("");
    try {
      const supportChat = await getSupportChatForUser(user.uid);
      if (supportChat?.id) {
        navigate(`/messages/${encodeURIComponent(supportChat.id)}`);
      } else {
        setSupportFallbackNotice("No active support conversation yet. Opening Inbox so you can start a new message.");
        const timer = setTimeout(() => navigate("/messages"), 700);
        setFallbackNavTimer(timer);
      }
    } catch (err) {
      // Fail closed to inbox route to avoid surfacing runtime overlays on intermittent auth/rules issues.
      errorDev("Operation failed:", err);
      setSupportFallbackNotice("No active support conversation yet. Opening Inbox so you can start a new message.");
      navigate("/messages");
    } finally {
      setSupportChatLoading(false);
    }
  }, [navigate, user?.uid]);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-neutral-950">
      {/* ── Header ─────────────────────────────────────────── */}
      <div className="bg-white dark:bg-neutral-900 border-b border-gray-200 dark:border-neutral-800">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-5 sm:py-6">
          <div className="flex items-center gap-2">
            <Link
              to="/me"
              className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-gray-600 hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-600 dark:text-gray-300 dark:hover:bg-neutral-800"
              aria-label={safeT("back_to_profile", "Back to Profile")}
            >
              <ArrowLeft className="w-4 h-4" aria-hidden="true" />
            </Link>
            <h1 className="text-base font-semibold text-gray-900 dark:text-white">
              {safeT("title", "Help Center")}
            </h1>
          </div>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {safeT("subtitle", "Find answers and get support")}
          </p>

          {/* Search */}
          <div className="mt-4 relative max-w-xl">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder={safeT("search_placeholder", "Search for help...")}
              className="ui-input pl-10 pr-10"
              aria-label="Search help center"
            />
            {searchInput && (
              <button
                onClick={() => {
                  setSearchInput("");
                  setSearchQuery("");
                }}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                aria-label="Clear search"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-5 pb-24">
        {/* ── Search Results ───────────────────────────────── */}
        {searchResults ? (
          <div className="mb-6">
            <h2 className="text-base font-bold text-gray-900 dark:text-white mb-3">
              {safeT("search_results", "Results").replace("{{query}}", searchQuery)}
            </h2>
            {searchResults.guides.length === 0 && searchResults.faq.length === 0 ? (
              <NoData
                title={safeT("no_results", "No results found.")}
                description=""
              />
            ) : (
              <div className="space-y-3">
                {searchResults.guides.map((g) => (
                  <Link
                    key={g.id}
                    to={`/help/${g.id}`}
                    className="block ui-card rounded-xl p-4 hover:border-green-500 dark:hover:border-green-500 transition group"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-lg bg-green-100 dark:bg-green-900/30 flex items-center justify-center flex-shrink-0">
                        <BookOpen className="w-4 h-4 text-green-700 dark:text-green-400" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-sm font-semibold text-gray-900 dark:text-white truncate">{g.title}</h3>
                        <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{g.summary}</p>
                      </div>
                      <ChevronRight className="w-4 h-4 text-gray-400 group-hover:text-green-600 flex-shrink-0" />
                    </div>
                  </Link>
                ))}
                {searchResults.faq.map((f) => (
                  <div
                    key={f.id}
                    className="ui-card rounded-xl p-4"
                  >
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-white">{f.question}</h3>
                    <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">{f.answer}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          <>
            {/* ── Category Cards ─────────────────────────────── */}
            <section className="mb-6">
              <h2 className="text-base font-bold text-gray-900 dark:text-white mb-3">
                {safeT("categories_title", "Browse by Topic")}
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                {categories.map((cat) => {
                  const Icon = CATEGORY_ICONS[cat.id] || Compass;
                  const isActive = categoryFilter === cat.id;
                  return (
                    <button
                      key={cat.id}
                      onClick={() =>
                        setCategoryFilter(isActive ? null : cat.id)
                      }
                      className={`text-left rounded-xl border p-3 transition group ${
                        isActive
                          ? "border-green-500 bg-green-50 dark:bg-green-900/20 dark:border-green-600"
                          : "border-gray-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 hover:border-green-500 dark:hover:border-green-500"
                      }`}
                    >
                      <div
                        className={`w-9 h-9 rounded-lg flex items-center justify-center mb-2 ${
                          isActive
                            ? "bg-green-600 text-white"
                            : "bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400"
                        }`}
                      >
                        <Icon className="w-4 h-4" />
                      </div>
                      <h3 className="text-sm font-bold text-gray-900 dark:text-white leading-tight">
                        {cat.label}
                      </h3>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 line-clamp-2 leading-snug">
                        {cat.desc}
                      </p>
                    </button>
                  );
                })}
              </div>
            </section>

            {/* ── Tabs ──────────────────────────────────────── */}
            <div className="flex gap-1.5 mb-5 overflow-x-auto pb-1 -mx-1 px-1" role="tablist">
              {tabs.map((item) => (
                <button
                  key={item.id}
                  role="tab"
                  aria-selected={tab === item.id}
                  onClick={() => setTab(item.id)}
                  className={`flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-sm font-semibold whitespace-nowrap transition ${
                    tab === item.id
                      ? "bg-green-600 text-white"
                      : "bg-white dark:bg-neutral-800 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-neutral-700 hover:bg-gray-50 dark:hover:bg-neutral-700"
                  }`}
                >
                  <item.Icon className="w-4 h-4" />
                  {item.label}
                  {item.count > 0 && (
                    <span className={`ml-1 px-1.5 py-0.5 rounded-full text-[10px] ${
                      tab === item.id ? "bg-white/20 text-white" : "bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400"
                    }`}>
                      {item.count}
                    </span>
                  )}
                </button>
              ))}
            </div>

            {/* ── Tab Content ──────────────────────────────── */}
            <div aria-live="polite">
              {/* Guides Tab */}
              {tab === "guides" && (
                <section className="space-y-3">
                  {guides.length === 0 ? (
                    <NoData
                      title={safeT("no_results", "No guides found.")}
                      description=""
                    />
                  ) : (
                    guides.map((guide) => {
                      const Icon = CATEGORY_ICONS[guide.category] || BookOpen;
                      return (
                        <Link
                          key={guide.id}
                          to={`/help/${guide.id}`}
                          className="flex items-center gap-4 ui-card rounded-xl p-4 hover:border-green-500 dark:hover:border-green-500 hover:shadow-sm transition group"
                        >
                          <div className="w-10 h-10 rounded-lg bg-green-100 dark:bg-green-900/30 flex items-center justify-center flex-shrink-0">
                            <Icon className="w-5 h-5 text-green-700 dark:text-green-400" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h3 className="text-sm font-bold text-gray-900 dark:text-white">
                              {guide.title}
                            </h3>
                            <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5 line-clamp-1">
                              {guide.summary}
                            </p>
                          </div>
                          <ArrowRight className="w-4 h-4 text-gray-400 group-hover:text-green-600 dark:group-hover:text-green-400 flex-shrink-0 transition" />
                        </Link>
                      );
                    })
                  )}
                </section>
              )}

              {/* FAQ Tab */}
              {tab === "faq" && (
                <section>
                  <FAQ items={faqItems} />
                </section>
              )}

              {/* Tickets Tab */}
              {tab === "tickets" && (
                <section>
                  <div className="ui-card rounded-xl p-4 mb-4 border border-blue-200 dark:border-blue-900/50 bg-blue-50 dark:bg-blue-950/20">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <h3 className="text-sm font-bold text-gray-900 dark:text-white">
                          Live Support Chat
                        </h3>
                        <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">
                          Continue a real-time conversation with Support Center in your inbox.
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={openLiveSupport}
                        disabled={supportChatLoading}
                        className="ui-btn-primary px-3 py-2 text-sm disabled:opacity-60"
                      >
                        {supportChatLoading ? "Opening..." : "Open Chat"}
                      </button>
                    </div>
                    {supportFallbackNotice && (
                      <div className="mt-3 rounded-lg border border-amber-200 dark:border-amber-900/50 bg-amber-50 dark:bg-amber-950/20 px-3 py-2 text-xs text-amber-800 dark:text-amber-200">
                        {supportFallbackNotice}
                      </div>
                    )}
                  </div>

                  {!user && !authLoading ? (
                    <div className="ui-card rounded-xl p-6 text-center">
                      <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                        {safeT("sign_in_to_view", "Sign in to view your tickets.")}
                      </p>
                      <div className="flex gap-3 justify-center">
                        <button
                          onClick={() => navigate("/login")}
                          className="ui-btn-secondary px-4 py-2 text-sm"
                        >
                          {safeT("contact_sign_in", "Sign in")}
                        </button>
                        <button
                          onClick={() => navigate("/signup")}
                          className="ui-btn-primary px-4 py-2 text-sm"
                        >
                          {safeT("contact_sign_up", "Create account")}
                        </button>
                      </div>
                    </div>
                  ) : ticketsLoading ? (
                    <div className="space-y-3 animate-pulse">
                      {[1, 2, 3].map((i) => (
                        <div key={i} className="ui-card rounded-xl p-4">
                          <div className="h-4 bg-gray-200 dark:bg-neutral-700 rounded w-3/4 mb-2" />
                          <div className="h-3 bg-gray-200 dark:bg-neutral-700 rounded w-1/2" />
                        </div>
                      ))}
                    </div>
                  ) : tickets.length === 0 ? (
                    <NoData
                      title={safeT("tickets_empty", "No tickets yet")}
                      description={safeT("tickets_empty_desc", "Open a ticket if you need help.")}
                    />
                  ) : (
                    <div className="space-y-3">
                      {tickets.slice(0, 10).map((t) => (
                        <TicketCard key={t.id} ticket={t} />
                      ))}
                      {tickets.length > 10 && (
                        <Link
                          to="/support/tickets"
                          className="block text-center text-sm text-green-600 dark:text-green-400 hover:underline py-2"
                        >
                          {safeT("view_all", "View all")} →
                        </Link>
                      )}
                    </div>
                  )}
                </section>
              )}

              {/* Contact Tab */}
              {tab === "contact" && (
                <section>
                  <div className="ui-card rounded-xl p-5">
                    <h2 className="text-sm font-bold text-gray-900 dark:text-white mb-1">
                      {safeT("contact_title", "Contact Support")}
                    </h2>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
                      {safeT("contact_subtitle", "Open a ticket and we'll respond within 24–72 hours.")}
                    </p>
                    <ContactForm />
                  </div>
                </section>
              )}
            </div>

            {/* ── Contact CTA ──────────────────────────────── */}
            {tab !== "contact" && (
              <div className="mt-6 bg-green-50 dark:bg-green-900/20 rounded-xl border border-green-200 dark:border-green-800 p-5 flex items-center justify-between gap-4">
                <div>
                  <h3 className="text-sm font-bold text-gray-900 dark:text-white">
                    {safeT("contact_cta", "Need help?")}
                  </h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    {safeT("contact_cta_desc", "Our support team is ready to assist you.")}
                  </p>
                </div>
                <button
                  onClick={() => setTab("contact")}
                  className="ui-btn-primary flex-shrink-0 px-4 py-2 text-sm"
                >
                  {safeT("contact_cta_button", "Contact Support")}
                </button>
              </div>
            )}
          </>
        )}


      </div>
    </div>
  );
}
